import {createSelector} from "@reduxjs/toolkit";
import {dcpcApi} from "../../Services/api/dcpcApi";

const SOURCE_TYPES_TAG = 'SourceTypes';

dcpcApi.enhanceEndpoints({ addTagTypes: [SOURCE_TYPES_TAG] });

const sourceTypesApi = dcpcApi.injectEndpoints({
    endpoints: (builder) => ({
        getTemplates: builder.query({
            query: () => ({
                url: 'outputforms/template',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getFilesInfo: builder.query({
            query: () => ({
                url: 'archive/materials',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getMaterialById: builder.query({
            query: (id) => ({
                url: 'archive/materials/'+id,
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getStationForFilters: builder.query({
            query: () => ({
                url: 'constructor/filters/stations',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getDatasourceTypesForFilters: builder.query({
            query: () => ({
                url: 'constructor/filters/datasourcetypes',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getDatasourceTypesByStation:  builder.query({
            query: (id) => `constructor/filters/datasourcetypes/${id}`,
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getDatasourcesForFilters: builder.query({
            query: () => ({
                url: 'constructor/filters/datasources',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getCodesForFilters: builder.query({
            query: () => ({
                url: 'constructor/filters/codes',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getOperationsFilters: builder.query({
            query: () => ({
                url: 'constructor/filters/operations',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        getEquationsFilters: builder.query({
            query: () => ({
                url: 'constructor/filters/equations',
                method: 'get',
            }),
            providesTags: [SOURCE_TYPES_TAG],
        }),
        createFilter: builder.mutation({
            query: (filter) => ({
                method: 'POST',
                url: 'templates/decade',
                body: filter
            }),
        }),
        createTemporaryTurnTemplate: builder.mutation({
            query: (filter) => ({
                method: 'POST',
                url: 'templates/temporary_turn',
                body: filter
            }),
        }),
    }),
});

export const {
    useGetDatasourceTypesByStationQuery,
    useGetTemplatesQuery,
    useGetOperationsFiltersQuery,
    useGetEquationsFiltersQuery,
    useGetStationForFiltersQuery,
    useGetFilesInfoQuery,
    useGetMaterialsByIdQuery,
    useGetDatasourceTypesForFiltersQuery,
    useGetDatasourcesForFiltersQuery,
    useGetCodesForFiltersQuery,
    useCreateFilterMutation,
    useCreateTemporaryTurnTemplateMutation
} = sourceTypesApi;


export const selectTemplates = (state) => sourceTypesApi.endpoints.getTemplates.select()(state)
export const selectorTemplates = (state) => selectTemplates(state).data || []


export const selectStationsForFilters = (state) => sourceTypesApi.endpoints.getStationForFilters.select()(state)
export const selectorStationsForFilters = (state) => selectStationsForFilters(state).data || [] //{id: 375, name: "ЗУША-НОВОСИЛЬ", sourcesTypes: []}

export const selectOperations = (state) => sourceTypesApi.endpoints.getOperationsFilters.select()(state);
export const selectorOperations = (state) => selectOperations(state).data || [];

export const selectEquations = (state) => sourceTypesApi.endpoints.getEquationsFilters.select()(state);
export const selectorEquations = (state) => selectEquations(state).data || [];

export const selectCodes = (state) => sourceTypesApi.endpoints.getCodesForFilters.select()(state);
export const selectorCodesWithExactDatasource = (state, row) => getCodesWithExactDatasource(state, row)

export const selectEditable = (state) => state.constructors.decade.editable


function getCodesWithExactDatasource(state, row) {
    const all = selectCodes(state).data || [];

    if (row === undefined) {
        console.log("row is undefined!")
        return []
    }

    const ids = row.datasource.codes

    return all.filter(x => ids.includes(x.id))
}

export const operationsSelector = createSelector(
    selectorOperations,
    (operation) => operation,
);

export const equationsSelector = createSelector(
    selectorEquations,
    (equation) => equation,
);


export const selectSelectedStation = (state) => state.constructors.decade.station;

export const temporaryTurnSelector = (state) => state.constructors.temporaryTurn;

export const decadeConstructor = (state) => state.constructors.decade;

export const filtersSelector = (state) => state.constructors.decade.filtersRows;

function findIndexByRowId(arr, rowId) {
    for (let i=0; i<arr.length; i++) {
        if (arr[i].rowId == rowId) {
            return i
        }
    }

    return -1;
}

function selectRow(state, filters, rowNum) {
    const index = findIndexByRowId(filters, rowNum)
    return filters[index];
}

export const rowSelector = (state, filters, rowNum) => selectRow(state, filters, rowNum);

export const selectDatasourceTypes = (state) => sourceTypesApi.endpoints.getDatasourceTypesForFilters.select()(state)
export const selectorDatasourceTypes = (state, constructor) => getDatasourceTypesForStation(state, constructor) || []

function getDatasourceTypesForStation(state, constructor) {
    const all = selectDatasourceTypes(state).data || []


    const ids = constructor.station.sourcesTypes

    return all.filter(x => ids.includes(x.id))
}

export const selectDatasources = (state) => sourceTypesApi.endpoints.getDatasourcesForFilters.select()(state)
export const selectorDatasourcesWithExactType = (state, row) => getDatasourcesWithExactType(state, row) || []

function getDatasourcesWithExactType(state, row) {
    const all = selectDatasources(state).data || [];

    if (row === undefined) {
        return all
    }

    const ids = row.type.datasources

    return all.filter(x => ids.includes(x.id))
}


export const possibleDatasourceTypesSelector = createSelector(
    decadeConstructor,
    selectorDatasourceTypes,
    (constr, types) => combineDatasourcesTypesAndTypesForStation(constr, types)
)

function combineDatasourcesTypesAndTypesForStation(constr, types) {
    const typesInConstr = constr.station.sourcesTypes
    const resTypes = []

    for (let i=0;i<types.length; i++) {
        if (typesInConstr.includes(types[i].id)) {
            resTypes.push(types[i])
        }
    }
    return resTypes
}



